import React, { useMemo, useState } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock } from '@fortawesome/pro-light-svg-icons/faLock';
import { TypedMemo } from '../../common/types';
import { isSocialPeriodLocked, SOCIAL_PERIODS, SocialPeriodDays } from '../promote/socialPeriod';
import { PeriodUpgradePrompt } from './PeriodUpgradePrompt';
import { socialMessages as messages } from './socialMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet per screen; each part of the screen uses its own slice of it */
import styles from './Social.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface SocialPeriodPickerProps {
  days: SocialPeriodDays;
  /** The history window of the plan in days. A longer period shows a lock. */
  historyDaysLimit: number;
  onChange: (days: SocialPeriodDays) => void;
}

/**
 * The period picker: 7d, 28d, 90d, 180d, 365d. A period past the history window of the plan is
 * not disabled: a click opens the upgrade prompt below the picker (design b2).
 */
const SocialPeriodPickerComponent: React.FC<SocialPeriodPickerProps> = ({ days, historyDaysLimit, onChange }) => {
  const intl = useIntl();
  const [lockedDays, setLockedDays] = useState<SocialPeriodDays>();

  const options = useMemo(
    () =>
      SOCIAL_PERIODS.map((option) => {
        const isLocked = isSocialPeriodLocked(option, historyDaysLimit);
        const pick = () => {
          if (isLocked) {
            // A second click on the same locked period closes the prompt.
            setLockedDays((current) => (current === option ? undefined : option));
            return;
          }
          setLockedDays(undefined);
          onChange(option);
        };
        return (
          <button
            key={option}
            type="button"
            role="radio"
            aria-checked={option === days}
            data-locked={isLocked ? 'true' : undefined}
            className={clsx(option === days && styles.periodOn, isLocked && styles.periodLocked)}
            onClick={pick}
          >
            {isLocked ? <FontAwesomeIcon icon={faLock} /> : null}
            {intl.formatMessage(messages.periodDays, { days: option })}
          </button>
        );
      }),
    [days, historyDaysLimit, intl, onChange]
  );

  return (
    <div className={styles.periodWrap}>
      <div className={styles.period} role="radiogroup" aria-label={intl.formatMessage(messages.periodLabel)}>
        {options}
      </div>
      {lockedDays === undefined ? null : <PeriodUpgradePrompt days={lockedDays} />}
    </div>
  );
};

export const SocialPeriodPicker = TypedMemo(SocialPeriodPickerComponent);
